import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { FaEdit, FaPlus } from "react-icons/fa";
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// const API_BASE = "http://localhost:5000/api";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000/api";

const getAllowedFaculties = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.role === 'admin') return null;
    return Array.isArray(user.allowed_faculties) ? user.allowed_faculties : [];
  } catch { return []; }
};
const getAllowedProgramTypes = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.role === 'admin') return ["diploma", "bachelor", "postgraduate"];
    return Array.isArray(user.allowed_program_types) ? user.allowed_program_types : ["bachelor"];
  } catch { return ["bachelor"]; }
};

function statusStyle(status) {
  switch (status) {
    case "ناجح":               return { color: "#16a34a", bg: "" };
    case "ملحق":               return { color: "#0369a1", bg: "#eff6ff" };
    case "تعليق دراسة":       return { color: "#b45309", bg: "#fffbeb" };
    case "إعادة":              return { color: "#ea580c", bg: "#fff7ed" };
    case "فصل":                return { color: "#7c3aed", bg: "#fdf4ff" };
    case "فصل (إعادة ثانية)": return { color: "#7c3aed", bg: "#fdf4ff" };
    default:                   return { color: "#6b7280", bg: "" };
  }
}

function usePostgradProgramsSmartList() {
  const [programs, setPrograms] = useState([]);
  const fetchPrograms = async () => {
    try {
      const res = await fetch(`${API_BASE}/postgraduate-programs`);
      const data = await res.json();
      setPrograms(Array.isArray(data) ? data : []);
    } catch { setPrograms([]); }
  };
  return { programs, fetchPrograms };
}

/* =========================================================
   أدوات حساب "السمستر" (عرض فقط - لا تؤثر على القيم الفعلية
   ولا على أي شيء يُرسل للباك إند: level_name و term_name
   يفضلوا كما هما تماماً، السمستر مجرد تمثيل/ليبل في الفرونت)
   ========================================================= */

// خرائط تحويل الأرقام العربية المكتوبة (المستوى/الدفعة) إلى رقم
const ORDINAL_WORD_TO_NUM = {
  "الأول": 1, "اول": 1, "أول": 1,
  "الثاني": 2, "ثاني": 2,
  "الثالث": 3, "ثالث": 3,
  "الرابع": 4, "رابع": 4,
  "الخامس": 5, "خامس": 5,
  "السادس": 6, "سادس": 6,
  "السابع": 7, "سابع": 7,
  "الثامن": 8, "ثامن": 8,
  "التاسع": 9, "تاسع": 9,
  "العاشر": 10, "عاشر": 10,
};

const NUM_TO_ORDINAL_WORD = {
  1: "الأولى",
  2: "الثانية",
  3: "الثالثة",
  4: "الرابعة",
  5: "الخامسة",
  6: "السادسة",
  7: "السابعة",
  8: "الثامنة",
  9: "التاسعة",
  10: "العاشرة",
};

// تحويل رقم السمستر إلى صيغة كلامية تُستخدم في العرض/الطباعة
// (السمستر 1 -> "السمستر الأول"، السمستر 2 -> "السمستر الثاني"...)
const SEMESTER_NUM_TO_WORD = {
  1: "الأول",
  2: "الثاني",
  3: "الثالث",
  4: "الرابع",
  5: "الخامس",
  6: "السادس",
  7: "السابع",
  8: "الثامن",
  9: "التاسع",
  10: "العاشر",
};

const NUM_TO_LEVEL_LABEL = {
  1: "المستوى الأول",
  2: "المستوى الثاني",
  3: "المستوى الثالث",
  4: "المستوى الرابع",
  5: "المستوى الخامس",
};

// يحاول استخراج رقم المستوى/الدفعة من نص عربي (يدعم كلمات وأرقام مكتوبة بالأرقام)
const extractOrdinalNumber = (text) => {
  if (!text) return null;
  const t = String(text).trim();

  const digitMatch = t.match(/\d+/);
  if (digitMatch) {
    const n = parseInt(digitMatch[0], 10);
    if (!isNaN(n) && n > 0) return n;
  }

  for (const [word, num] of Object.entries(ORDINAL_WORD_TO_NUM)) {
    if (t.includes(word)) return num;
  }

  return null;
};

// يحاول استخراج رقم الفصل (1 أو 2 أو 3) من term_name
const extractTermNumber = (termName) => {
  if (!termName) return null;
  const t = String(termName).trim();
  if (t.includes("أول")) return 1;
  if (t.includes("ثان")) return 2;
  if (t.includes("ثالث")) return 3;
  return null;
};

// يحسب رقم السمستر من (المستوى/الدفعة + الفصل). يرجع null لو تعذر الاستخراج
const computeSemesterNumber = (levelOrBatch, termName) => {
  const levelNum = extractOrdinalNumber(levelOrBatch);
  const termNum = extractTermNumber(termName);
  if (!levelNum || !termNum) return null;
  return (levelNum - 1) * 2 + termNum;
};

// يبني Label نصي للسمستر بصيغة "السمستر الأول" (وليس "السمستر 1")
const getSemesterWordLabel = (levelOrBatch, termName) => {
  const sem = computeSemesterNumber(levelOrBatch, termName);
  if (!sem) return null;
  const word = SEMESTER_NUM_TO_WORD[sem] || sem; // fallback للرقم لو تجاوز القائمة
  return `السمستر ${word}`;
};

// يحول رقم السمستر إلى { levelNum, termNum } بناءً على عدد الفصول لكل مستوى/دفعة
const semesterToLevelTerm = (semesterNumber, termsPerLevel = 2) => {
  const semNum = parseInt(semesterNumber, 10);
  if (!semNum || semNum < 1) return null;
  const levelNum = Math.floor((semNum - 1) / termsPerLevel) + 1;
  const termNum = ((semNum - 1) % termsPerLevel) + 1;
  return { levelNum, termNum };
};

// يبني قائمة خيارات السمستر (1..N) لاستخدامها في dropdown
const buildSemesterList = (programType, termsPerLevel) => {
  const tpl = termsPerLevel || (programType === "postgraduate" ? 3 : 2);
  const maxLevel = 5;
  const list = [];
  for (let lvl = 1; lvl <= maxLevel; lvl++) {
    for (let term = 1; term <= tpl; term++) {
      const semNum = (lvl - 1) * tpl + term;
      list.push({ value: semNum, levelNum: lvl, termNum: term });
    }
  }
  return list;
};

// يرجع اسم الفصل النصي (الفصل الأول/الثاني/الثالث) من رقم الفصل
const termNumToName = (termNum) => {
  if (termNum === 1) return "الفصل الأول";
  if (termNum === 2) return "الفصل الثاني";
  if (termNum === 3) return "الفصل الثالث";
  return "";
};

// يرجع اسم المستوى (للبكالوريوس/الدبلوم) من رقم المستوى
const levelNumToName = (levelNum) => {
  return NUM_TO_LEVEL_LABEL[levelNum] || "";
};

// يرجع اسم الدفعة (للدراسات العليا) من رقمها، بصيغة "الدفعة <ترتيب>"
const batchNumToName = (batchNum) => {
  const word = NUM_TO_ORDINAL_WORD[batchNum];
  return word ? `الدفعة ${word}` : "";
};

/**
 * SemesterPicker: حقل عرض/اختيار "السمستر" - للعرض ولإعادة تعبئة المستوى/الفصل تلقائياً.
 * لا يغيّر أي شيء غير level/term من خلال onPick (لا يُرسل أي قيمة "سمستر" منفصلة للباك إند).
 * الليبل المعروض دايماً بصيغة كلامية: "السمستر الأول" وليس "السمستر 1".
 *
 * props:
 *  - levelOrBatch: القيمة الحالية للمستوى أو الدفعة (نص)
 *  - termName: القيمة الحالية للفصل (نص)
 *  - programType: "bachelor" | "diploma" | "postgraduate"
 *  - onPick(levelOrBatchName, termName): يُستدعى عند اختيار سمستر من القائمة
 *  - disabled: تعطيل الحقل
 */
function SemesterPicker({ levelOrBatch, termName, programType, onPick, disabled }) {
  const isPostgrad = programType === "postgraduate";
  const termsPerLevel = isPostgrad ? 3 : 2;

  const currentSemester = computeSemesterNumber(levelOrBatch, termName);
  const currentSemesterWord = currentSemester ? (SEMESTER_NUM_TO_WORD[currentSemester] || currentSemester) : null;

  const semesterList = useMemo(
    () => buildSemesterList(programType, termsPerLevel),
    [programType, termsPerLevel]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const semNum = parseInt(val, 10);
    const parsed = semesterToLevelTerm(semNum, termsPerLevel);
    if (!parsed) return;

    const newTermName = termNumToName(parsed.termNum);
    const newLevelOrBatch = isPostgrad
      ? batchNumToName(parsed.levelNum)
      : levelNumToName(parsed.levelNum);

    onPick(newLevelOrBatch, newTermName);
  };

  return (
    <div className="input-group">
      <label className="input-label">السمستر</label>
      <select
        className="input-field"
        value={currentSemester || ""}
        onChange={handleChange}
        disabled={disabled}
      >
        <option value="">
          {currentSemesterWord ? `السمستر ${currentSemesterWord}` : "— غير محدد —"}
        </option>
        {semesterList.map((opt) => {
          const word = SEMESTER_NUM_TO_WORD[opt.value] || opt.value;
          return (
            <option key={opt.value} value={opt.value}>
              السمستر {word}
            </option>
          );
        })}
      </select>
    </div>
  );
}

const TermResult = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [faculties,    setFaculties]    = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [periods,      setPeriods]      = useState([]);
  const [yearOptions,  setYearOptions]  = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [termOptions,  setTermOptions]  = useState([]);
  const pgSmart = usePostgradProgramsSmartList();

  const [selectedFacultyId,    setSelectedFacultyId]    = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [programType,          setProgramType]          = useState("bachelor");
  const [postgraduateProgram,  setPostgraduateProgram]  = useState("");
  const [academicYear,         setAcademicYear]         = useState("");
  const [levelName,            setLevelName]            = useState("");
  const [termName,             setTermName]             = useState("");

  const [savedRows,       setSavedRows]       = useState([]);
  const [skippedRows,     setSkippedRows]     = useState([]);
  const [hasSavedResults, setHasSavedResults] = useState(false);
  const [recommendations, setRecommendations] = useState({});
  const [savingRecs,      setSavingRecs]      = useState(false);
  const [detailedGrades,     setDetailedGrades]     = useState({});
  const [repeatedCoursesMap, setRepeatedCoursesMap] = useState({});
  const [loadingDetails,     setLoadingDetails]     = useState(false);
  const [loadingFaculties, setLoadingFaculties] = useState(false);
  const [loadingDeps,      setLoadingDeps]      = useState(false);
  const [loadingPeriods,   setLoadingPeriods]   = useState(false);
  const [computingResult,  setComputingResult]  = useState(false);
  const [loadingResult,    setLoadingResult]    = useState(false);

  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [openRecStudentId, setOpenRecStudentId] = useState(null);

  const [showStats, setShowStats] = useState(false);

const [courseStats, setCourseStats] = useState({});

  const canPickDepartment  = !!selectedFacultyId;
  const canPickProgramType = !!selectedDepartmentId;
  const canProceedAfterProgram =
    programType === "bachelor" || programType === "diploma"
      ? true : !!postgraduateProgram.trim();
  const canPickYear  = canPickProgramType && canProceedAfterProgram;
  const canPickLevel = !!academicYear.trim();
  const canPickTerm  = !!levelName.trim();
  const canComputeTerm =
    selectedFacultyId && selectedDepartmentId && canProceedAfterProgram &&
    academicYear.trim() && levelName.trim() && termName.trim();

  // ليبل السمستر بصيغة كلامية (للعرض في الفورم والطباعة فقط - لا تأثير على أي بيانات مُرسلة)
  const currentSemesterLabel = getSemesterWordLabel(levelName, termName);

  const countByStatus = (s) => savedRows.filter(r => r.result_status === s).length;

  // إجماليات الفصلين — من الباك مباشرة
  const sampleRow       = savedRows[0] || {};
  const bothTotalHours  = Number(sampleRow.both_total_hours  || 0);
  const bothOneThird    = bothTotalHours > 0 ? (bothTotalHours / 3).toFixed(1)     : "—";
  const bothTwoThirds   = bothTotalHours > 0 ? (bothTotalHours * 2 / 3).toFixed(1) : "—";

  const termTotalHours = Number(sampleRow.term_total_hours || 0);
  const termTwoThirds  = termTotalHours > 0 ? (termTotalHours * 2 / 3).toFixed(1) : "—";

  useEffect(() => {
    const load = async () => {
      setLoadingFaculties(true);
      try {
        const res = await fetch(`${API_BASE}/faculties-list`);
        const all = await res.json();
        const allowed  = getAllowedFaculties();
        const filtered = allowed === null ? all : all.filter(f => allowed.includes(f.id));
        setFaculties(Array.isArray(filtered) ? filtered : []);
        if (filtered.length === 0 && allowed !== null)
          showToast("لا توجد كليات مسموح لك الوصول إليها", "error");
      } catch { showToast("مشكلة في تحميل الكليات", "error"); }
      finally { setLoadingFaculties(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (programType === "postgraduate") pgSmart.fetchPrograms();
    else setPostgraduateProgram("");
  }, [programType]);

  useEffect(() => {
    if (savedRows.length === 0) return;
    const map = {};
    savedRows.forEach(r => { map[r.student_id] = r.recommendation || ""; });
    setRecommendations(map);
  }, [savedRows]);

  useEffect(() => {
    if (savedRows.length === 0 || !canComputeTerm) { setRepeatedCoursesMap({}); return; }
    const run = async () => {
      const map = {};
      for (const s of savedRows) {
        const params = new URLSearchParams({
          student_id: s.student_id, academic_year: academicYear.trim(),
          level_name: levelName.trim(), term_name: termName.trim(), program_type: programType,
          ...(programType === "postgraduate" && postgraduateProgram.trim()
            ? { postgraduate_program: postgraduateProgram.trim() } : {})
        });
        try {
          const res  = await fetch(`${API_BASE}/student-repeated-courses?${params}`);
          const data = res.ok ? await res.json() : {};
          map[s.student_id] = Array.isArray(data.repeated) ? data.repeated : [];
        } catch { map[s.student_id] = []; }
      }
      setRepeatedCoursesMap(map);
    };
    run();
  }, [savedRows, canComputeTerm]);

  useEffect(() => {
    if (savedRows.length === 0 || !canComputeTerm) { setDetailedGrades({}); return; }
    const run = async () => {
      setLoadingDetails(true);
      const map = {};
      for (const s of savedRows) {
        const params = new URLSearchParams({
          student_id: s.student_id, academic_year: academicYear.trim(),
          level_name: levelName.trim(), term_name: termName.trim(), program_type: programType,
          ...(programType === "postgraduate" && postgraduateProgram.trim()
            ? { postgraduate_program: postgraduateProgram.trim() } : {})
        });
        try {
          const res  = await fetch(`${API_BASE}/student-term-grades?${params}`);
          const data = res.ok ? await res.json() : [];
          map[s.student_id] = Array.isArray(data) ? data : [];
        } catch { map[s.student_id] = []; }
      }
      setDetailedGrades(map);
      setLoadingDetails(false);
    };
    run();
  }, [savedRows, canComputeTerm]);

const saveRecommendations = async () => {
  if (Object.keys(recommendations).length === 0) {
    return showToast("لا توجد ملاحظات للحفظ", "error");
  }

  setSavingRecs(true);
  try {
    const token = sessionStorage.getItem("token");

    const payload = Object.entries(recommendations).map(([student_id, recommendation]) => ({
      student_id: Number(student_id),
      faculty_id: Number(selectedFacultyId),
      department_id: Number(selectedDepartmentId),
      academic_year: academicYear.trim(),
      level_name: levelName.trim(),
      term_name: termName.trim(),
      program_type: programType,
      postgraduate_program: programType === "postgraduate" ? postgraduateProgram.trim() : null,
      recommendation: recommendation.trim()
    }));

    const res = await fetch(`${API_BASE}/term-results/save-recommendations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ recommendations: payload })
    });

        if (res.status === 401) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      showToast("انتهت الجلسة، يرجى تسجيل الدخول", "error");
      navigate("/login");
      return; 
    }

    const data = await res.json();

    if (!res.ok) throw new Error(data.error || "فشل الحفظ");

    showToast("تم الحفظ", "success");

    await loadSavedResult();

  } catch (e) {
    console.error(e);
    showToast(e.message || "خطأ في الحفظ", "error");
  } finally {
    setSavingRecs(false);
  }
};

// حساب إحصائيات كل مادة
useEffect(() => {
  if (Object.keys(detailedGrades).length === 0) {
    setCourseStats({});
    return;
  }

  const stats = {};

  Object.values(detailedGrades).flat().forEach(grade => {
    const course = grade.course_name || "غير معروف";
    if (!stats[course]) {
      stats[course] = { total: 0, passed: 0, failed: 0 };
    }

    stats[course].total++;

    const mark = Number(grade.total_mark || 0);
    const letter = String(grade.grade_letter || "").toUpperCase();

    if (letter === "F" || mark < 50) {
      stats[course].failed++;
    } else {
      stats[course].passed++;
    }
  });

  setCourseStats(stats);
}, [detailedGrades]);

  const fetchAcademicPeriods = async (pType, pgProg) => {
    setLoadingPeriods(true);
    try {
      const pt = (pType || "bachelor").trim(), pg = (pgProg || "").trim();
      let url = `${API_BASE}/academic-periods?program_type=${encodeURIComponent(pt)}`;
      if (pt === "postgraduate" && pg) url += `&postgraduate_program=${encodeURIComponent(pg)}`;
      const res  = await fetch(url);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      setPeriods(rows);
      setYearOptions(Array.from(new Set(rows.map(r => (r.academic_year||"").trim()).filter(Boolean))));
    } catch { setPeriods([]); setYearOptions([]); setLevelOptions([]); setTermOptions([]); }
    finally { setLoadingPeriods(false); }
  };

  const rebuildOptions = (rows, year, level) => {
    const y=(year||"").trim(), l=(level||"").trim();
    setLevelOptions(Array.from(new Set(
      rows.filter(r=>(r.academic_year||"").trim()===y).map(r=>(r.level_name||"").trim()).filter(Boolean)
    )));
    setTermOptions(Array.from(new Set(
      rows.filter(r=>(r.academic_year||"").trim()===y&&(r.level_name||"").trim()===l)
          .map(r=>(r.term_name||"").trim()).filter(Boolean)
    )));
  };

  useEffect(() => { rebuildOptions(periods, academicYear, levelName); }, [periods, academicYear, levelName]);
  useEffect(() => {
    setAcademicYear(""); setLevelName(""); setTermName(""); setSavedRows([]); setSkippedRows([]);
    if (selectedDepartmentId) fetchAcademicPeriods(programType, postgraduateProgram);
  }, [programType, postgraduateProgram]);
  useEffect(() => { if (programType !== "postgraduate") setPostgraduateProgram(""); }, [programType]);

  const resetBelowFaculty = () => {
    setDepartments([]); setSelectedDepartmentId("");
    setProgramType("bachelor"); setPostgraduateProgram("");
    setAcademicYear(""); setLevelName(""); setTermName("");
    setSavedRows([]); setSkippedRows([]); setRecommendations({});
  };
  const resetBelowDepartment = () => {
    setProgramType("bachelor"); setPostgraduateProgram("");
    setAcademicYear(""); setLevelName(""); setTermName("");
    setSavedRows([]); setSkippedRows([]); setRecommendations({});
  };
  const onSelectFaculty    = (id) => { setSelectedFacultyId(id);    resetBelowFaculty();     if (id) fetchDepts(id); };
  const onSelectDepartment = (id) => { setSelectedDepartmentId(id); resetBelowDepartment();  if (id) fetchAcademicPeriods(programType, postgraduateProgram); };
  const fetchDepts = async (fid) => {
    setLoadingDeps(true);
    try { const res=await fetch(`${API_BASE}/departments/${fid}`); const d=await res.json(); setDepartments(Array.isArray(d)?d:[]); }
    catch { setDepartments([]); showToast("مشكلة في تحميل الأقسام","error"); }
    finally { setLoadingDeps(false); }
  };

  const computeAndSaveResult = async () => {
    if (!canComputeTerm) return showToast("كمّل الاختيارات أولاً","error");
    if (hasSavedResults && !window.confirm("النتائج محفوظة بالفعل.\n\nهل تريد إعادة الحساب؟")) return;
    setComputingResult(true); setSkippedRows([]); setSavedRows([]);
    try {
      const res = await fetch(`${API_BASE}/term-results/calculate-save`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          faculty_id: Number(selectedFacultyId), department_id: Number(selectedDepartmentId),
          program_type: programType,
          postgraduate_program: programType==="postgraduate"?postgraduateProgram.trim():null,
          academic_year: academicYear.trim(), level_name: levelName.trim(), term_name: termName.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error||"فشل حساب النتيجة");
      setSavedRows(Array.isArray(data.saved)?data.saved:[]);
      setSkippedRows(Array.isArray(data.skipped)?data.skipped:[]);
      setHasSavedResults(true);
      showToast(data.message||"تم حساب النتائج");
    } catch (e) { showToast(e.message||"مشكلة في حساب النتيجة","error"); }
    finally { setComputingResult(false); }
  };

  const loadSavedResult = async () => {
    if (!canComputeTerm) return;
    setLoadingResult(true); setSkippedRows([]); setSavedRows([]);
    try {
      const qs =
        `faculty_id=${encodeURIComponent(selectedFacultyId)}` +
        `&department_id=${encodeURIComponent(selectedDepartmentId)}` +
        `&program_type=${encodeURIComponent(programType)}` +
        (programType==="postgraduate"?`&postgraduate_program=${encodeURIComponent(postgraduateProgram.trim())}`:`&postgraduate_program=`) +
        `&academic_year=${encodeURIComponent(academicYear.trim())}` +
        `&level_name=${encodeURIComponent(levelName.trim())}` +
        `&term_name=${encodeURIComponent(termName.trim())}`;
      const res  = await fetch(`${API_BASE}/term-results/list?${qs}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error||"فشل تحميل النتيجة");
      const rows = Array.isArray(data)?data:[];
      setSavedRows(rows); setHasSavedResults(rows.length>0);
    } catch (e) { showToast(e.message||"مشكلة في تحميل النتيجة","error"); }
    finally { setLoadingResult(false); }
  };

  useEffect(() => { if (canComputeTerm) loadSavedResult(); }, [canComputeTerm]);

const printResults = (mode = 'letter') => {
  if (savedRows.length === 0) {
    return showToast("لا توجد نتائج للطباعة بعد", "error");
  }

  const isLetterMode = mode === 'letter';

  let programTypeText = "";
  if (programType === "diploma") programTypeText = "دبلوم";
  else if (programType === "bachelor") programTypeText = "بكالوريوس";
  else if (programType === "postgraduate") programTypeText = "دراسات عليا";

  const programNameText = (programType === "postgraduate" && postgraduateProgram.trim())
    ? ` - ${postgraduateProgram.trim()}`
    : "";

  // ليبل السمستر بصيغة كلامية (مثال: "السمستر الأول") - للعرض في الطباعة فقط
  const semesterLabel = getSemesterWordLabel(levelName, termName);
  const semesterLineText = semesterLabel ? ` | ${semesterLabel}` : "";

  const honorStudents = savedRows
    .filter(r => Number(r.term_gpa || 0) >= 3.00)
    .sort((a, b) => Number(b.cumulative_gpa || 0) - Number(a.cumulative_gpa || 0));

  const allStudents = [...savedRows].sort((a, b) => Number(b.term_gpa || 0) - Number(a.term_gpa || 0));

  const facultyName = faculties.find(f => f.id === Number(selectedFacultyId))?.faculty_name || "غير محدد";
  const departmentName = departments.find(d => d.id === Number(selectedDepartmentId))?.department_name || "غير محدد";

  const commonHeader = `
    <div style="text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 1px solid #ccc;">
      <h1 style="margin: 0; color: #0a3753; font-size: 22px;">
        جامعة بورتسودان الأهلية
      </h1>
      <p style="margin: 8px 0 4px; font-weight: bold; font-size: 16px;">
        ${facultyName} - ${departmentName}
      </p>
      <p style="margin: 4px 0; font-size: 14px; font-weight: bold;">
        ${programTypeText}${programNameText}
      </p>
      <p style="margin: 4px 0; font-size: 14px;">
        السنة الدراسية: ${academicYear} | المستوى: ${levelName} | الفصل: ${termName}${semesterLineText}
      </p>
    </div>
  `;

  // صفحة الشرف
  let honorPage = '';
  if (honorStudents.length > 0) {
    honorPage = `
      <div style="direction: rtl; font-family: 'Janna LT', sans-serif; padding: 30px; font-size: 14px; break-after: page; page-break-after: always;">
        ${commonHeader}
        <h2 style="color: #0a3753; text-align: center; margin: 35px 0 25px; font-size: 18px;">
          قائمة الشرف
        </h2>
        <table style="width: 100%; border-collapse: collapse; margin-top: 15px; break-inside: avoid; page-break-inside: avoid;">
          <thead>
            <tr style="background: #6e6e6e; color: white;">
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">#</th>
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الاسم</th>
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الرقم الجامعي</th>
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الموقف الأكاديمي</th>
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">GPA فصلي</th>
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">GPA تراكمي</th>
            </tr>
          </thead>
          <tbody>
            ${honorStudents.map((r, i) => `
              <tr style="background: ${i % 2 === 0 ? '#f8f9fa' : '#ffffff'}; break-inside: avoid;">
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${i + 1}</td>
                <td style="padding: 11px; border: 1px solid #ddd;">${r.full_name}</td>
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${r.university_id}</td>
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${r.academic_status || 'غير محدد'}</td>
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${Number(r.term_gpa || 0).toFixed(2)}</td>
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${Number(r.cumulative_gpa || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  // صفحة النتائج العامة
  const mainResultsPage = `
    <div style="direction: rtl; font-family: 'Janna LT', sans-serif; padding: 30px; font-size: 14px; break-after: page;">
      ${commonHeader}
      <h2 style="color: #0a3753; text-align: center; margin: 35px 0 25px; font-size: 18px;">
        نتائج الطلاب
      </h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 15px; break-inside: avoid; page-break-inside: avoid;">
        <thead>
          <tr style="background: #6e6e6e; color: white;">
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">#</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الاسم</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الرقم الجامعي</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الموقف الأكاديمي</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">GPA فصلي</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">GPA تراكمي</th>
          </tr>
        </thead>
        <tbody>
          ${allStudents.map((r, i) => `
            <tr style="background: ${i % 2 === 0 ? '#ffffff' : '#f8f9fa'}; break-inside: avoid;">
              <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${i + 1}</td>
              <td style="padding: 11px; border: 1px solid #ddd;">${r.full_name}</td>
              <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${r.university_id}</td>
              <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${r.academic_status || 'غير محدد'}</td>
              <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${Number(r.term_gpa || 0).toFixed(2)}</td>
              <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${Number(r.cumulative_gpa || 0).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;

  // تحضير المواد
  const allCourses = new Set();
  Object.values(detailedGrades).forEach(grades => {
    grades.forEach(grade => {
      if (grade.course_name) allCourses.add(grade.course_name);
    });
  });
  const uniqueCourses = Array.from(allCourses).sort();

  if (uniqueCourses.length === 0) {
    showToast("لا توجد أسماء مواد متاحة للعرض", "error");
    return;
  }

  // صفحة التفاصيل
  const detailsPage = `
    <div style="direction: rtl; font-family: 'Janna LT', sans-serif; padding: 30px; font-size: 14px;">
      ${commonHeader}
      <h2 style="color: #0a3753; text-align: center; margin: 35px 0 25px; font-size: 18px;">
        تفاصيل درجات المواد ${isLetterMode ? '(الحرفية)' : '(الرقمية)'}
      </h2>

      <table style="width: 100%; border-collapse: collapse; margin-top: 15px; break-inside: auto;">
        <thead>
          <tr style="background: #6e6e6e; color: white;">
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">#</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الاسم</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الرقم الجامعي</th>
            <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">الموقف الأكاديمي</th>
            ${uniqueCourses.map(courseName => `
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px; text-align: center;">
                ${courseName}
              </th>
            `).join('')}

            ${Object.values(repeatedCoursesMap).some(arr => arr.length > 0) ? `
              <th style="padding: 12px; border: 1px solid #ccc; font-size: 13px;">إزالة الرسوب</th>
            ` : ''}
          </tr>
        </thead>
        <tbody>
          ${allStudents.map((r, i) => {
            const studentGrades = detailedGrades[r.student_id] || [];
            const gradesMap = {};
            studentGrades.forEach(grade => {
              if (grade.course_name) gradesMap[grade.course_name] = grade;
            });

            const repeatedData = repeatedCoursesMap[r.student_id] || [];
            let repeatedDisplay = "—";
            if (repeatedData.length > 0) {
              repeatedDisplay = repeatedData.map(item => {
              const displayValue = isLetterMode 
              ? (item.grade_letter || '—') 
              : (item.total_mark ?? '—');
              return `${item.course_name}: <strong>${displayValue}</strong>`;
            }).join("<br>");
            }

            return `
              <tr style="background: ${i % 2 === 0 ? '#ffffff' : '#f8f9fa'}; break-inside: avoid;">
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${i + 1}</td>
                <td style="padding: 11px; border: 1px solid #ddd;">${r.full_name}</td>
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${r.university_id}</td>
                <td style="padding: 11px; border: 1px solid #ddd; text-align: center;">${r.academic_status || 'غير محدد'}</td>

                ${uniqueCourses.map(courseName => {
                  const grade = gradesMap[courseName];
                  if (!grade) {
                    return `<td style="padding: 11px; border: 1px solid #ddd; text-align: center;">—</td>`;
                  }

                  const displayValue = isLetterMode 
                    ? (grade.grade_letter || '—') 
                    : (grade.total_mark ?? '—');

                  return `
                    <td style="padding: 11px; border: 1px solid #ddd; text-align: center; font-weight: bold; font-size: 13px;">
                      ${displayValue}
                    </td>
                  `;
                }).join('')}

                ${Object.values(repeatedCoursesMap).some(arr => arr.length > 0) ? `
                  <td style="padding: 11px; border: 1px solid #ddd; text-align: right; font-size: 12px; font-weight: bold; line-height: 1.6;">
                    ${repeatedDisplay}
                  </td>
                ` : ''}
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  `;

  const fullContent = honorPage + mainResultsPage + detailsPage;

  const element = document.createElement('div');
  element.innerHTML = fullContent;

  const options = {
    margin: 0.5,
    filename: `نتائج_${isLetterMode ? 'حرفية' : 'رقمية'}_${academicYear.replace('/', '-')}_${termName.replace(/ /g, '_')}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, logging: false },
    jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };

  html2pdf().from(element).set(options).save();

  showToast(`جاري إنشاء ملف PDF ${isLetterMode ? 'الحرفي' : 'الرقمي'}...`, "success");
};

const exportToExcel = (mode = 'letter') => {
  if (savedRows.length === 0) {
    return showToast("لا توجد نتائج للتصدير", "error");
  }

  const isLetterMode = mode === 'letter';
  const fileSuffix = isLetterMode ? 'حرفية' : 'رقمية';

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([]);

  let row = 1;

  // ==================== الهيدر الرئيسي ====================
  const facultyName = faculties.find(f => f.id === Number(selectedFacultyId))?.faculty_name || "غير محدد";
  const departmentName = departments.find(d => d.id === Number(selectedDepartmentId))?.department_name || "غير محدد";

  let programTypeText = programType === "diploma" ? "دبلوم" :
                        programType === "bachelor" ? "بكالوريوس" : "دراسات عليا";

  // ليبل السمستر بصيغة كلامية (مثال: "السمستر الأول") - للعرض في التصدير فقط
  const semesterLabel = getSemesterWordLabel(levelName, termName);
  const semesterLineSuffix = semesterLabel ? ` | ${semesterLabel}` : "";

  const header = [
    ["جامعة بورتسودان الأهلية"],
    [`${facultyName} - ${departmentName}`],
    [`${programTypeText}`],
    [`${academicYear} | ${levelName} | ${termName}${semesterLineSuffix}`],
    []
  ];

  XLSX.utils.sheet_add_aoa(ws, header, { origin: "A1" });

  // دمج الهيدر الرئيسي (من العمود A إلى G)
  if (!ws['!merges']) ws['!merges'] = [];
  for (let i = 0; i <= 4; i++) {
    ws['!merges'].push({ s: { r: i, c: 0 }, e: { r: i, c: 6 } });
  }

  row = 7;   // بعد الهيدر + سطر فارغ

  // ==================== قائمة الشرف ====================
  const honorStudents = savedRows
    .filter(r => Number(r.term_gpa || 0) >= 3.00)
    .sort((a, b) => Number(b.cumulative_gpa || 0) - Number(a.cumulative_gpa || 0));

  if (honorStudents.length > 0) {
    XLSX.utils.sheet_add_aoa(ws, [["قائمة الشرف"]], { origin: `A${row}` });
    ws['!merges'].push({ s: { r: row-1, c: 0 }, e: { r: row-1, c: 6 } }); // دمج
    row++;

    const honorHeader = ["#", "الاسم", "الرقم الجامعي", "الموقف الأكاديمي", "GPA فصلي", "GPA تراكمي"];
    XLSX.utils.sheet_add_aoa(ws, [honorHeader], { origin: `A${row}` });
    row++;

    honorStudents.forEach((r, i) => {
      XLSX.utils.sheet_add_aoa(ws, [[
        i + 1, r.full_name, r.university_id, r.academic_status || "—",
        Number(r.term_gpa || 0).toFixed(2), Number(r.cumulative_gpa || 0).toFixed(2)
      ]], { origin: `A${row}` });
      row++;
    });
    row += 3; // مسافة أكبر بين الأقسام
  }

  // ==================== نتائج الطلاب ====================
  XLSX.utils.sheet_add_aoa(ws, [["نتائج الطلاب"]], { origin: `A${row}` });
  ws['!merges'].push({ s: { r: row-1, c: 0 }, e: { r: row-1, c: 6 } });
  row++;

  const mainHeader = ["#", "الاسم", "الرقم الجامعي", "الموقف الأكاديمي", "حالة النتيجة", "GPA فصلي", "GPA تراكمي"];
  XLSX.utils.sheet_add_aoa(ws, [mainHeader], { origin: `A${row}` });
  row++;

  savedRows.sort((a, b) => Number(b.term_gpa || 0) - Number(a.term_gpa || 0))
    .forEach((r, i) => {
      XLSX.utils.sheet_add_aoa(ws, [[
        i + 1,
        r.full_name,
        r.university_id,
        r.academic_status || "—",
        r.result_status || "—",
        Number(r.term_gpa || 0).toFixed(2),
        Number(r.cumulative_gpa || 0).toFixed(2)
      ]], { origin: `A${row}` });
      row++;
    });

  row += 3;

  // ==================== تفاصيل الدرجات ====================
  const allCourses = new Set();
  Object.values(detailedGrades).forEach(grades => {
    grades.forEach(g => g.course_name && allCourses.add(g.course_name));
  });
  const uniqueCourses = Array.from(allCourses).sort();

  XLSX.utils.sheet_add_aoa(ws, [[`تفاصيل الدرجات ${isLetterMode ? '(حرفية)' : '(رقمية)'}`]], { origin: `A${row}` });
  ws['!merges'].push({ s: { r: row-1, c: 0 }, e: { r: row-1, c: 6 } }); // دمج
  row++;

 const detailHeader = ["#", "الاسم", "الرقم الجامعي", "الموقف الأكاديمي", ...uniqueCourses, "إزالة الرسوب"];
  XLSX.utils.sheet_add_aoa(ws, [detailHeader], { origin: `A${row}` });
  row++;

  savedRows.forEach((r, i) => {
    const studentGrades = detailedGrades[r.student_id] || [];
    const gradesMap = {};
    studentGrades.forEach(g => {
      if (g.course_name) gradesMap[g.course_name] = g;
    });

    const rowData = [
      i + 1,
      r.full_name,
      r.university_id,
      r.academic_status || "—"
    ];

    uniqueCourses.forEach(course => {
      const grade = gradesMap[course];
      rowData.push(grade 
        ? (isLetterMode ? (grade.grade_letter || "—") : (grade.total_mark ?? "—")) 
        : "—"
      );
    });


    const repeatedData = repeatedCoursesMap[r.student_id] || [];
const repeatedCell = repeatedData.length > 0
  ? repeatedData.map(item => 
      `${item.course_name}: ${isLetterMode ? (item.grade_letter || '—') : (item.total_mark ?? '—')}`
    ).join(" | ")
  : "—";
rowData.push(repeatedCell);

    XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: `A${row}` });
    row++;
  });

  // ==================== تنسيق نهائي ====================
  ws['!cols'] = [
    { wch: 5 }, 
    { wch: 28 }, 
    { wch: 15 }, 
    { wch: 14 }, 
    { wch: 14 },
    ...uniqueCourses.map(() => ({ wch: 13 })),
    { wch: 30 } 
  ];

  XLSX.utils.book_append_sheet(wb, ws, `نتائج_${fileSuffix}`);

  const fileName = `نتائج_${fileSuffix}_${academicYear.replace('/', '-')}_${termName.replace(/ /g, '_')}.xlsx`;
  XLSX.writeFile(wb, fileName);

  showToast(`تم تصدير Excel ${isLetterMode ? 'الحرفي' : 'الرقمي'} بنجاح `, "success");
};

  return (
    <div className="admission-layout">
      <header className="library-header">
        <div className="library-header-title"><span style={{fontSize:22}}></span><span> حساب النتيجة</span></div>
        <button onClick={()=>navigate(-1)} style={{background:"none",border:"none",cursor:"pointer",color:"white",fontSize:"26px",display:"flex",alignItems:"center"}} title="رجوع"><IoArrowBack /></button>
      </header>

      <main className="library-main">
        <div className="library-container">

          <div className="card" style={{marginTop:14}}>
            <h2 className="card-title">اختيار الفصل</h2>
            <div className="two-col-grid" style={{marginBottom:12}}>

              <div className="input-group">
                <label className="input-label">الكلية</label>
                <select className="input-field" value={selectedFacultyId} onChange={e=>onSelectFaculty(e.target.value)} disabled={loadingFaculties}>
                  <option value="">{loadingFaculties?"جارٍ التحميل...":"— اختار —"}</option>
                  {faculties.map(f=><option key={f.id} value={f.id}>{f.faculty_name}</option>)}
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">القسم</label>
                <select className="input-field" value={selectedDepartmentId} onChange={e=>onSelectDepartment(e.target.value)} disabled={!canPickDepartment||loadingDeps}>
                  <option value="">{!canPickDepartment?"اختار كلية أولاً":loadingDeps?"جارٍ التحميل...":"— اختار —"}</option>
                  {departments.map(d=><option key={d.id} value={d.id}>{d.department_name}</option>)}
                </select>
              </div>

              <div className="input-group" style={{gridColumn:"1 / -1"}}>
                <label className="input-label">نوع البرنامج</label>
                <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                  {getAllowedProgramTypes().map(type=>(
                    <label key={type} style={{display:"flex",gap:8,alignItems:"center",fontWeight:700,opacity:!canPickProgramType?0.6:1,cursor:!canPickProgramType?"not-allowed":"pointer"}}>
                      <input type="radio" name="pgTypeResult" value={type} checked={programType===type} onChange={e=>setProgramType(e.target.value)} disabled={!canPickProgramType}/>
                      {type==="diploma"&&"دبلوم"}{type==="bachelor"&&"بكالوريوس"}{type==="postgraduate"&&"دراسات عليا"}
                    </label>
                  ))}
                </div>
              </div>

              {programType==="postgraduate"&&(
                <div className="input-group" style={{gridColumn:"1 / -1"}}>
                  <label className="input-label">اسم برنامج الدراسات العليا</label>
                  <input className="input-field" dir="rtl" list="pg_res" placeholder="مثال: ماجستير إدارة أعمال"
                    value={postgraduateProgram} onChange={e=>setPostgraduateProgram(e.target.value)} disabled={!canPickProgramType}/>
                  <datalist id="pg_res">{pgSmart.programs.map((p,i)=><option key={i} value={p}/>)}</datalist>
                </div>
              )}

              <div className="input-group">
                <label className="input-label">السنة الدراسية</label>
                <input className="input-field" dir="rtl" list="yr_res" placeholder="مثال: 2024/2025" value={academicYear}
                  onChange={e=>{setAcademicYear(e.target.value);setLevelName("");setTermName("");setSavedRows([]);}} disabled={!canPickYear||loadingPeriods}/>
                <datalist id="yr_res">{yearOptions.map(x=><option key={x} value={x}/>)}</datalist>
              </div>

              <div className="input-group">
                <label className="input-label">{programType==="postgraduate"?"الدفعة":"المستوى"}</label>
                <input className="input-field" dir="rtl" list="lv_res"
                  placeholder={programType==="postgraduate"?"مثال: الدفعة الأولى":"مثال: المستوى الأول"} value={levelName}
                  onChange={e=>{setLevelName(e.target.value);setTermName("");setSavedRows([]);}} disabled={!canPickLevel}/>
                <datalist id="lv_res">{levelOptions.map(x=><option key={x} value={x}/>)}</datalist>
              </div>

              <div className="input-group">
                <label className="input-label">الفصل الدراسي</label>
                <input className="input-field" dir="rtl" list="tm_res" placeholder="مثال: الفصل الأول" value={termName}
                  onChange={e=>{setTermName(e.target.value);setSavedRows([]);}} disabled={!canPickTerm}/>
                <datalist id="tm_res">{termOptions.map(x=><option key={x} value={x}/>)}</datalist>
              </div>

              {/* حقل السمستر - للعرض/الاختيار السريع فقط، يملأ المستوى والفصل تلقائياً
                  ولا يُرسل أي قيمة "سمستر" منفصلة لأي API */}
              <SemesterPicker
                levelOrBatch={levelName}
                termName={termName}
                programType={programType}
                disabled={!canPickLevel}
                onPick={(newLevel, newTerm) => {
                  setLevelName(newLevel);
                  setTermName(newTerm);
                  setSavedRows([]);
                }}
              />
            </div>
          </div>

          <div className="card" style={{marginTop:14}}>
            <h2 className="card-title">حساب النتيجة (GPA + تصنيف)</h2>

            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
              <button className="btn btn-primary" onClick={computeAndSaveResult} disabled={!canComputeTerm||computingResult}>
                {computingResult?"جاري الحساب...":"حساب النتيجة"}
              </button>
{savedRows.length > 0 && (<>
<div style={{ position: "relative", display: "inline-block" }}>
  <button
    className="btn btn-primary"
    onClick={() => setShowPrintMenu(prev => !prev)}
    disabled={loadingDetails}
  >
     طباعة / تصدير النتيجة ▾
  </button>

  {showPrintMenu && (
    <div style={{
      position: "absolute",
      top: "110%",
      right: 0,
      background: "#fff",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
      zIndex: 999,
      minWidth: 220,
      overflow: "hidden"
    }}>
      <button onClick={() => { printResults('letter'); setShowPrintMenu(false); }}
        style={{display:"block", width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"right", cursor:"pointer", fontWeight:700, color:"#1e4a6e", borderBottom:"1px solid #f3f4f6"}}
        onMouseEnter={e => e.target.style.background = "#eff6ff"}
        onMouseLeave={e => e.target.style.background = "none"}>
         PDF - نتيجة حرفية
      </button>

      <button onClick={() => { printResults('numeric'); setShowPrintMenu(false); }}
        style={{display:"block", width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"right", cursor:"pointer", fontWeight:700, color:"#1e4a6e", borderBottom:"1px solid #f3f4f6"}}
        onMouseEnter={e => e.target.style.background = "#eff6ff"}
        onMouseLeave={e => e.target.style.background = "none"}>
         PDF - نتيجة رقمية
      </button>

      <button onClick={() => { exportToExcel('letter'); setShowPrintMenu(false); }}
        style={{display:"block", width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"right", cursor:"pointer", fontWeight:700, color:"#166534", borderBottom:"1px solid #f3f4f6"}}
        onMouseEnter={e => e.target.style.background = "#eff6ff"}
        onMouseLeave={e => e.target.style.background = "none"}>
         Excel - نتيجة حرفية
      </button>

      <button onClick={() => { exportToExcel('numeric'); setShowPrintMenu(false); }}
        style={{display:"block", width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"right", cursor:"pointer", fontWeight:700, color:"#166534"}}
        onMouseEnter={e => e.target.style.background = "#eff6ff"}
        onMouseLeave={e => e.target.style.background = "none"}>
         Excel - نتيجة رقمية
      </button>
    </div>
  )}
</div>

  {/* زر الإحصائيات */}
  <button
    className="btn"
    onClick={() => setShowStats(!showStats)}
    style={{
      background: showStats ? "#0a998d" : "#0f766e",
      color: "#fff", fontWeight: 700
    }}
  >
    {showStats ? "إخفاء الإحصائيات" : "عرض الإحصائيات"}
  </button>
</>)}
              <span style={{color:"#6b7280",fontWeight:800,alignSelf:"center"}}>عدد النتائج: {savedRows.length}</span>
            </div>

            {/* بطاقات الحالات */}
            {savedRows.length>0&&(
              <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
                {[
                  {label:"ناجح",color:"#16a34a",bg:"#f0fdf4"},
                  {label:"ملحق",color:"#0369a1",bg:"#eff6ff"},
                  {label:"تعليق دراسة",color:"#b45309",bg:"#fffbeb"},
                  {label:"إعادة",color:"#ea580c",bg:"#fff7ed"},
                  {label:"فصل",color:"#7c3aed",bg:"#fdf4ff"},
                ].map(item=>{
                  const cnt=countByStatus(item.label);
                  if(cnt===0)return null;
                  return <div key={item.label} style={{background:item.bg,border:`1px solid ${item.color}`,borderRadius:8,padding:"6px 14px",fontSize:13,fontWeight:700}}>
                    <span style={{color:item.color}}>{item.label}: {cnt}</span>
                  </div>;
                })}
{/* {bothTotalHours>0&&(
  <div style={{background:"#f3f4f6",border:"1px solid #d1d5db",borderRadius:8,padding:"6px 12px",fontSize:11,color:"#374151"}}>
    حد الإعادة: {bothOneThird}–{bothTwoThirds}س &nbsp;|&nbsp; 
    حد الفصل: &gt;{termTwoThirds}س
  </div>
)} */}
              </div>
            )}

            {skippedRows.length>0&&(
              <div style={{marginTop:10,overflowX:"auto"}}>
                <div style={{color:"#b91c1c",fontWeight:900,marginBottom:8}}>يوجد (مواد/درجات ناقصة)</div>
                <table className="simple-table" style={{width:"100%"}}>
                  <thead><tr><th>#</th><th>الطالب</th><th>الرقم الجامعي</th><th>الموقف الأكاديمي</th><th>المواد الناقصة</th><th>السبب</th></tr></thead>
                  <tbody>{skippedRows.map((m,idx)=>(
                    <tr key={idx}><td>{idx+1}</td><td>{m.full_name}</td><td>{m.university_id}</td>
                      <td>{m.academic_status}</td><td>{m.missing_courses}</td><td>{m.reason}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}

            <div style={{overflowX:"auto",marginTop:10}}>
              {!canComputeTerm?(
                <p style={{color:"#6b7280"}}>كمّل اختيار الفصل أولاً.</p>
              ):computingResult||loadingResult?(
                <p>جارٍ التحميل...</p>
              ):savedRows.length===0?(
                <p style={{color:"#6b7280"}}>لا توجد نتيجة محفوظة/محسوبة لهذا الفصل بعد.</p>
              ):(
<table style={{
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",   // ← المفتاح: يوزع الأعمدة بالتساوي
  fontSize: 11,
}}>
  <colgroup>
    <col style={{width:"3%"}}/>   {/* # */}
    <col style={{width:"12%"}}/>  {/* الاسم */}
    <col style={{width:"8%"}}/>   {/* الرقم الجامعي */}
    <col style={{width:"7%"}}/>   {/* الموقف الأكاديمي */}
    <col style={{width:"7%"}}/>   {/* حالة النتيجة */}
    <col style={{width:"6%"}}/>   {/* GPA فصلي */}
    <col style={{width:"6%"}}/>   {/* GPA تراكمي */}
    <col style={{width:"6%"}}/>   {/* نقاط الفصل */}
    <col style={{width:"6%"}}/>   {/* ساعات الفصل */}
    <col style={{width:"6%"}}/>   {/* رسوب الفصل */}
    <col style={{width:"7%"}}/>   {/* ساعات الفصلين */}
    <col style={{width:"7%"}}/>   {/* رسوب الفصلين */}
    <col style={{width:"15%"}}/>  {/* ملاحظات */}
  </colgroup>
  <thead>
    <tr style={{background:"#1e4a6e", color:"#fff"}}>
      {[
        "#","الاسم","الرقم الجامعي","الموقف الأكاديمي","حالة النتيجة",
        "GPA فصلي","GPA تراكمي","نقاط الفصل","ساعات الفصل",
        "رسوب الفصل","ساعات الفصلين","رسوب الفصلين"," ملاحظات"
      ].map(h => (
        <th key={h} style={{
          padding:"6px 4px", border:"1px solid #e5e7eb",
          fontSize:10, fontWeight:700, textAlign:"center",
          wordBreak:"break-word", whiteSpace:"normal"
        }}>{h}</th>
      ))}
    </tr>
  </thead>
  <tbody>
    {savedRows.map((r, idx) => {
      const {color, bg} = statusStyle(r.result_status);
      const cellStyle = {
        padding:"5px 4px", border:"1px solid #e5e7eb",
        textAlign:"center", fontSize:11,
        wordBreak:"break-word", whiteSpace:"normal"
      };
      return (
        <tr key={`${r.student_id}-${idx}`} style={{background: idx%2===0 ? bg||"#fff" : bg||"#f9fafb"}}>
          <td style={cellStyle}>{idx+1}</td>
          <td style={{...cellStyle, textAlign:"right", paddingRight:6}}>{r.full_name}</td>
          <td style={cellStyle}>{r.university_id}</td>
          <td style={cellStyle}>{r.academic_status}</td>
          <td style={cellStyle}>
            <span style={{color, fontWeight:"bold", fontSize:12}}>{r.result_status||"—"}</span>
          </td>
          <td style={cellStyle}>{r.term_gpa ?? "—"}</td>
          <td style={cellStyle}>{r.cumulative_gpa ?? "—"}</td>
          <td style={cellStyle}>{r.term_total_points ?? "—"}</td>
          <td style={cellStyle}>{r.term_total_hours ?? "—"}</td>
          <td style={{...cellStyle, color: Number(r.failed_hours||0)>0 ? '#ea580c':'#19bb54', fontWeight:'bold'}}>
            {r.failed_hours ?? "—"}
          </td>
          <td style={cellStyle}>{r.both_total_hours ?? "—"}</td>
          <td style={{...cellStyle, color: Number(r.both_failed_hours||0)>0 ? '#ea580c':'#19bb54', fontWeight:'bold'}}>
            {r.both_failed_hours ?? "—"}
          </td>
<td style={{...cellStyle, textAlign: "center"}}>
  {openRecStudentId === r.student_id ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <textarea
        rows={2}
        autoFocus
        value={recommendations[r.student_id] || ""}
        onChange={e => setRecommendations(prev => ({...prev, [r.student_id]: e.target.value}))}
        placeholder="اكتب الملاحظة هنا.."
        style={{
          width: "100%", fontSize: 10, border: "1px solid #d1d5db",
          borderRadius: 4, padding: "2px 4px", resize: "vertical",
          fontFamily: "inherit", direction: "rtl", lineHeight: 1.4
        }}
      />
      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
        <button
          onClick={async () => {
            await saveRecommendations();
            setOpenRecStudentId(null);
          }}
          disabled={savingRecs}
          style={{
            fontSize: 10, padding: "2px 8px", background: "#0a3753",
            color: "#fff", border: "none", borderRadius: 4,
            cursor: "pointer", fontWeight: 700
          }}
        >
          {savingRecs ? "..." : " حفظ"}
        </button>
        <button
          onClick={() => setOpenRecStudentId(null)}
          style={{
            fontSize: 10, padding: "2px 8px", background: "#e5e7eb",
            color: "#374151", border: "none", borderRadius: 4,
            cursor: "pointer"
          }}
        >
          ✕
        </button>
      </div>
    </div>
  ) : (
    <button
      onClick={() => setOpenRecStudentId(r.student_id)}
      title={recommendations[r.student_id] ? "عدّل الملاحظة" : "أضف ملاحظة"}
      style={{
        background: recommendations[r.student_id] ? "#fef3c7" : "#f3f4f6",
        border: `1px solid ${recommendations[r.student_id] ? "#f59e0b" : "#d1d5db"}`,
        borderRadius: 6, cursor: "pointer", padding: "3px 8px",
        fontSize: 14, color: recommendations[r.student_id] ? "#92400e" : "#6b7280"
      }}
    >
      {recommendations[r.student_id] ? <FaEdit size={14} /> : <FaPlus size={14} />}
    </button>
  )}
</td>
        </tr>
      );
    })}
  </tbody>
</table>
              )}
{/* ====================== قسم الإحصائيات والرسوم البيانية ====================== */}
{showStats && Object.keys(courseStats).length > 0 && (
  <div className="card" style={{ marginTop: 20, background: "#f8fafc" }}>
    <h3 style={{ 
      color: "#0a3753", 
      marginBottom: 24, 
      textAlign: "center", 
      fontSize: "19px" 
    }}>
       إحصائيات النجاح والرسوب حسب المادة
    </h3>

    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",   // ← يسمح بـ 5 في الصف
      gap: 16
    }}>
      {Object.entries(courseStats).map(([courseName, data]) => {
        const passRate = data.total > 0 ? ((data.passed / data.total) * 100).toFixed(1) : 0;

        const barData = {
          labels: [courseName],
          datasets: [
            { label: "ناجحين", data: [data.passed], backgroundColor: "#16a34a" },
            { label: "راسبين", data: [data.failed], backgroundColor: "#ea580c" },
          ],
        };

        const doughnutData = {
          labels: ["ناجحين", "راسبين"],
          datasets: [{
            data: [data.passed, data.failed],
            backgroundColor: ["#16a34a", "#ea580c"],
            borderWidth: 2,
          }],
        };

        return (
          <div key={courseName} style={{
            background: "#fff",
            borderRadius: 10,
            padding: 12,
            boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
            border: "1px solid #e2e8f0"
          }}>
            <h4 style={{ 
              marginBottom: 12, 
              color: "#1e2937", 
              fontSize: 14.5, 
              fontWeight: 700,
              textAlign: "center"
            }}>
              {courseName}
            </h4>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Bar Chart - صغير جداً */}
              <div style={{ height: "110px" }}>
                <Bar 
                  data={barData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { 
                      legend: { display: false } 
                    },
                    scales: {
                      y: { beginAtZero: true, ticks: { font: { size: 9 } } },
                      x: { ticks: { font: { size: 10 } } }
                    }
                  }} 
                />
              </div>

              {/* Doughnut + نسبة - أصغر */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: "95px", height: "95px" }}>
                  <Doughnut 
                    data={doughnutData} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "75%",
                      plugins: { legend: { display: false } }
                    }} 
                  />
                </div>

                <div style={{ marginTop: 8, textAlign: "center" }}>
                  <strong style={{ fontSize: 19, color: "#16a34a" }}>{passRate}%</strong>
                  <p style={{ margin: "2px 0 4px", fontSize: 12.5, color: "#64748b" }}>
                    نجاح
                  </p>
                  <p style={{ fontSize: 12.5, color: "#334155" }}>
                    {data.passed} / {data.total}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
)}
            </div>
          </div>
        </div>
      </main>

      {toast&&(
        <div className={"toast "+(toast.type==="error"?"toast-error":"toast-success")}>{toast.message}</div>
      )}
    </div>
  );
};

export default TermResult;